import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export type VoiceCommand =
  | "next"
  | "back"
  | "skip"
  | "stop"
  | "repeat"
  | "start"
  | null;

const COMMANDS: Record<Exclude<VoiceCommand, null>, RegExp> = {
  next: /\b(next|continue|go on|go ahead|that's all|thats all|done with this|move on)\b/,
  back: /\b(go back|previous|back)\b/,
  skip: /\b(skip( this)?|pass|no thanks|not now)\b/,
  stop: /\b(stop|pause|quiet|mute|that's enough|thats enough)\b/,
  repeat: /\b(repeat|say that again|what did you say|pardon)\b/,
  start: /\b(start|ready|yes|yeah|yep|ok|okay|let's go|lets go)\b/,
};

const SPOKEN_NUMBER_WORDS: Record<string, string> = {
  zero: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
  eleven: "11",
  twelve: "12",
  thirteen: "13",
  fourteen: "14",
  fifteen: "15",
  sixteen: "16",
  seventeen: "17",
  eighteen: "18",
  nineteen: "19",
  twenty: "20",
  first: "1",
  second: "2",
  third: "3",
  fourth: "4",
  fifth: "5",
  sixth: "6",
  seventh: "7",
  eighth: "8",
  ninth: "9",
  tenth: "10",
};

const PERSON_FILLER =
  /\b(coach|the coach|my coach|our coach|learner|the learner|my learner|student|the student|doctor|dr|professor|prof|please|um+|uh+|erm|like|its|it is|it s|that is|that s|i want|i would like|i d like|choose|pick|select|named|name is|called)\b/g;

export const normalizeSpeech = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\w\s:/.-]/g, " ")
    .replace(/\b(\d+)(?:st|nd|rd|th)\b/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .replace(
      /\b(zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)\b/g,
      (word) => SPOKEN_NUMBER_WORDS[word] || word,
    )
    .replace(/\b(\d+)\s+point\s+(\d+)\b/g, "$1.$2")
    .replace(/([a-z])[-/]+(\d)/g, "$1 $2")
    .replace(/\b(?:[a-z][\s.]+){1,}[a-z]\.?\b/g, (chunk) =>
      chunk.replace(/[\s.]+/g, ""),
    )
    .replace(/\s+/g, " ")
    .trim();

export const normalizePersonSpeech = (text: string) =>
  normalizeSpeech(text).replace(PERSON_FILLER, " ").replace(/\s+/g, " ").trim();

export const personNameAliases = (name: string, email?: string | null) => {
  const aliases = new Set<string>();
  const cleaned = name.trim();
  if (cleaned) {
    aliases.add(cleaned);
    const parts = cleaned.split(/\s+/).filter(Boolean);
    for (const part of parts) {
      aliases.add(part);
      const compact = part.replace(/[^a-z0-9]/gi, "");
      if (compact && compact !== part) aliases.add(compact);
    }
    if (parts.length > 1) {
      aliases.add(`${parts[0]} ${parts[parts.length - 1]}`);
    }
  }
  const local = (email || "").split("@")[0]?.trim();
  if (local) {
    aliases.add(local);
    aliases.add(local.replace(/[._+-]+/g, " "));
    const beforePlus = local.split("+")[0];
    if (beforePlus) aliases.add(beforePlus);
  }
  return [...aliases].filter(Boolean);
};

const REQUIREMENT_CODE =
  /\b(epa|iter|competenc(?:y|ies)|comp)\s*(\d+(?:\.\d+)?)\b/;

export const extractRequirementCode = (text: string) => {
  const spoken = normalizeSpeech(text);
  if (!spoken) return "";
  const prefixed = spoken.match(REQUIREMENT_CODE);
  if (prefixed) {
    const kind = prefixed[1].startsWith("comp") ? "competency" : prefixed[1];
    return `${kind} ${prefixed[2]}`;
  }
  const dotted = spoken.match(/\b(\d+\.\d+)\b/);
  return dotted?.[1] || "";
};

export const requirementNameAliases = (name: string, type?: string | null) => {
  const aliases = new Set<string>();
  const cleaned = name.trim();
  if (cleaned) {
    aliases.add(cleaned);
    const beforeColon = cleaned.split(":")[0].trim();
    if (beforeColon) {
      aliases.add(beforeColon);
      aliases.add(beforeColon.replace(/\s+/g, ""));
    }
  }
  const code =
    extractRequirementCode(cleaned) ||
    extractRequirementCode(`${type || ""} ${cleaned}`);
  if (code) {
    aliases.add(code);
    aliases.add(code.replace(/\s+/g, ""));
  }
  return [...aliases].filter(Boolean);
};

export const detectCommand = (text: string): VoiceCommand => {
  const spoken = normalizeSpeech(text);
  if (!spoken) return null;
  const ordered: Exclude<VoiceCommand, null>[] = [
    "stop",
    "repeat",
    "skip",
    "back",
    "next",
    "start",
  ];
  for (const command of ordered) {
    if (COMMANDS[command].test(spoken)) return command;
  }
  return null;
};

export const parseYesNo = (text: string): boolean | null => {
  const spoken = normalizeSpeech(text);
  if (/\b(yes|yeah|yep|yup|correct|that's right|thats right|i am|they are|with me)\b/.test(spoken)) {
    return true;
  }
  if (/\b(no|nope|not really|they're not|they are not|not here|not with me)\b/.test(spoken)) {
    return false;
  }
  return null;
};

const WORD_NUMBERS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
};

export const parseRating = (
  text: string,
  options: { value: number; label: string }[],
): number | null => {
  const spoken = normalizeSpeech(text);
  if (!spoken || options.length === 0) return null;

  const digit = spoken.match(/\b([1-6])\b/);
  if (digit) {
    const value = Number(digit[1]);
    if (options.some((option) => option.value === value)) return value;
  }

  for (const [word, value] of Object.entries(WORD_NUMBERS)) {
    if (new RegExp(`\\b${word}\\b`).test(spoken)) {
      if (options.some((option) => option.value === value)) return value;
    }
  }

  let best: { value: number; score: number } | null = null;
  for (const option of options) {
    const label = normalizeSpeech(option.label);
    if (!label) continue;
    if (spoken.includes(label) || label.includes(spoken)) {
      return option.value;
    }
    const score = tokenOverlap(spoken, label);
    if (score >= 0.45 && (!best || score > best.score)) {
      best = { value: option.value, score };
    }
  }
  return best?.value ?? null;
};

export const parseDate = (text: string, now = dayjs()): Dayjs | null => {
  const spoken = normalizeSpeech(text);
  if (!spoken) return null;
  if (/\btoday\b/.test(spoken)) return now.startOf("day");
  if (/\byesterday\b/.test(spoken)) return now.subtract(1, "day").startOf("day");
  if (/\btomorrow\b/.test(spoken)) return now.add(1, "day").startOf("day");

  const formats = [
    "MMMM D YYYY",
    "MMMM D",
    "MMM D YYYY",
    "MMM D",
    "M/D/YYYY",
    "M/D",
    "D MMMM YYYY",
    "D MMMM",
  ];
  for (const format of formats) {
    const parsed = dayjs(spoken, format, true);
    if (parsed.isValid()) {
      return parsed.year() === 2001 && !format.includes("Y")
        ? parsed.year(now.year())
        : parsed;
    }
  }

  const loose = dayjs(text);
  return loose.isValid() ? loose : null;
};

export const parseTime = (text: string): Dayjs | null => {
  const spoken = normalizeSpeech(text).replace(/\./g, "");
  if (!spoken) return null;

  const match = spoken.match(
    /\b(\d{1,2})(?::(\d{2}))?\s*(a\s*m|p\s*m|am|pm)?\b/,
  );
  if (!match) return null;

  let hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.replace(/\s/g, "");
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  if (hour > 23 || minute > 59) return null;
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

export type NamedOption = {
  id: string;
  name: string;
  aliases?: string[];
};

const uniqueSpokenForms = (text: string) => {
  const forms = [normalizeSpeech(text), normalizePersonSpeech(text)].filter(
    Boolean,
  );
  return [...new Set(forms)];
};

const scoreNameMatch = (spoken: string, name: string) => {
  if (!spoken || !name) return 0;
  if (spoken === name) return 1;
  const spokenCode = extractRequirementCode(spoken);
  const nameCode = extractRequirementCode(name);
  if (spokenCode && nameCode && spokenCode === nameCode) return 1;
  if (spoken.length >= 3 && name.length >= 3) {
    if (containsWholePhrase(spoken, name) || containsWholePhrase(name, spoken)) {
      const ratio =
        Math.min(spoken.length, name.length) /
        Math.max(spoken.length, name.length);
      if (ratio >= 0.45) return 0.78 + 0.17 * ratio;
    }
  }
  return Math.max(tokenOverlap(spoken, name), tokenFuzzyScore(spoken, name));
};

export const matchNamedOption = <T extends NamedOption>(
  text: string,
  options: T[],
): T | null => {
  const spokenForms = uniqueSpokenForms(text);
  if (spokenForms.length === 0 || options.length === 0) return null;

  const scored = options
    .map((option) => {
      const names = [option.name, ...(option.aliases || [])]
        .map(normalizeSpeech)
        .filter(Boolean);
      let score = 0;
      for (const spoken of spokenForms) {
        for (const name of names) {
          score = Math.max(score, scoreNameMatch(spoken, name));
          if (score === 1) return { option, score };
        }
      }
      return { option, score };
    })
    .filter((row) => row.score >= 0.55)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) return null;
  if (scored.length > 1 && scored[0].score - scored[1].score < 0.1) {
    return null;
  }
  return scored[0].option;
};

export const matchNamedOptions = <T extends NamedOption>(
  text: string,
  options: T[],
): T[] => {
  const spokenForms = uniqueSpokenForms(text);
  return options.filter((option) => {
    const names = [option.name, ...(option.aliases || [])]
      .map(normalizeSpeech)
      .filter(Boolean);
    return names.some((name) =>
      spokenForms.some((spoken) => scoreNameMatch(spoken, name) >= 0.55),
    );
  });
};

const containsWholePhrase = (haystack: string, needle: string) => {
  if (!needle) return false;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?:^|\\s)${escaped}(?:\\s|:|$)`).test(haystack);
};

const keepSpeechToken = (token: string) =>
  token.length > 1 || /^\d+(?:\.\d+)?$/.test(token);

const tokenOverlap = (left: string, right: string) => {
  const leftTokens = new Set(left.split(" ").filter(keepSpeechToken));
  const rightTokens = right.split(" ").filter(keepSpeechToken);
  if (leftTokens.size === 0 || rightTokens.length === 0) return 0;
  const hits = rightTokens.filter((token) => leftTokens.has(token)).length;
  return hits / Math.max(leftTokens.size, rightTokens.length);
};

const levenshtein = (left: string, right: string) => {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;
  const row = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    let previous = i - 1;
    row[0] = i;
    for (let j = 1; j <= right.length; j += 1) {
      const current = row[j];
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + cost);
      previous = current;
    }
  }
  return row[right.length];
};

const editSimilarity = (left: string, right: string) => {
  const max = Math.max(left.length, right.length);
  if (!max) return 0;
  return 1 - levenshtein(left, right) / max;
};

const soundex = (word: string) => {
  const letters = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!letters) return "";
  const codes: Record<string, string> = {
    b: "1",
    f: "1",
    p: "1",
    v: "1",
    c: "2",
    g: "2",
    j: "2",
    k: "2",
    q: "2",
    s: "2",
    x: "2",
    z: "2",
    d: "3",
    t: "3",
    l: "4",
    m: "5",
    n: "5",
    r: "6",
  };
  let encoded = letters[0].toUpperCase();
  let previous = codes[letters[0]] || "";
  for (let i = 1; i < letters.length && encoded.length < 4; i += 1) {
    const code = codes[letters[i]] || "";
    if (code && code !== previous) encoded += code;
    previous = code;
  }
  return `${encoded}000`.slice(0, 4);
};

const tokenSimilarity = (spokenToken: string, nameToken: string) => {
  const edit = editSimilarity(spokenToken, nameToken);
  const phonetic =
    nameToken.length >= 3 &&
    spokenToken.length >= 3 &&
    soundex(spokenToken) === soundex(nameToken)
      ? 0.8
      : 0;
  return Math.max(edit >= 0.72 ? edit : 0, phonetic);
};

const tokenFuzzyScore = (spoken: string, name: string) => {
  const spokenTokens = spoken.split(" ").filter(keepSpeechToken);
  const nameTokens = name.split(" ").filter(keepSpeechToken);
  if (spokenTokens.length === 0 || nameTokens.length === 0) return 0;

  let matched = 0;
  let matchedScore = 0;
  for (const spokenToken of spokenTokens) {
    let best = 0;
    for (const nameToken of nameTokens) {
      best = Math.max(best, tokenSimilarity(spokenToken, nameToken));
    }
    if (best > 0) {
      matched += 1;
      matchedScore += best;
    }
  }
  if (matched === 0) return 0;
  return (matchedScore / matched) * (matched / spokenTokens.length);
};
